import {
  Balloon,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Input,
  Message,
  Radio,
} from '@alifd/next';
import { Form, Modal, Select, Space, Table, Upload } from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import DownloadButton from '@/components/UI/DownloadButton';
import useService from '@/lib/hooks/useService';

import styles from './index.module.scss';

const { Item, useForm } = Form;
const RadioGroup = Radio.Group;

const modalBodyStyle = {
  height: '90vh',
  overflowY: 'scroll',
};

const formInitValues = {
  householdNumber: '',
  address: '',
  userName: '',
  familyMembers: [],
  usage: '',
  carNumber: [],
  effectiveDate: null,
  effectiveStatus: false,
  pingsNumber: '',
  housePhone: '',
  cellphone: '',
  company: '',
  rentMembers: '',
  buyStartTime: '',
  buyEndTime: '',
  doorNumber: '',
  mailNumber: '',
  securityIp: '',
  robotPosition: '',
  pay: '',
  isIgnoreFinanceBarcode: false,
};

const AddResidentAccountNumbers = () => {
  const [addForm] = useForm();
  const [editForm] = useForm();

  // 獲取redux user資料
  const user = useSelector((state) => state.user);
  const [allowedFuncs, setAllowedFuncs] = useState([]);
  useEffect(() => {
    // 避免非同步沒資料時出錯，使用if確保資料進來後才更新keys
    if (user.userGroup.key) {
      setAllowedFuncs(user.userGroup.key.checkedKeys);
    }
  }, [user]);
  const [
    residentAccountNumbers,
    updateResidentAccountNumbers,
    residentAccountNumbersService,
  ] = useService({
    collectionName: 'residentAccountNumbers',
  });
  const [parkingSpaces] = useService({
    collectionName: 'parkingSpaces',
  });

  const [addHouseholdNumberModal, setAddHouseholdNumberModal] = useState(false);
  const [editHouseholdNumberModal, setEditHouseholdNumberModal] =
    useState(false);
  const [isEffective, setIsEffective] = useState(false);
  const [residentAccountNumberId, setResidentAccountNumberId] = useState('');

  const today = moment();
  const yesterday = moment().subtract(1, 'days');

  // 查詢
  const funFind = useCallback(
    function (para) {
      return new Promise((resolve, reject) => {
        try {
          residentAccountNumbersService.find({ query: para }).then((result) => {
            resolve(result && result.data ? result.data : []);
          });
        } catch (error) {
          reject(error);
        }
      });
    },
    [residentAccountNumbersService]
  );
  // 新增
  function funCreate(obj) {
    return new Promise((resolve, reject) => {
      residentAccountNumbersService
        .create(obj)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // 下載範例檔案(Excel)
  function downloadExampleExcel() {
    return new Promise((resolve, reject) => {
      try {
        resolve((window.location.href = '/ezcon/api/getRANumbersExample'));
      } catch (error) {
        reject(error);
      }
    });
  }

  // 匯出檔案(Excel)
  function handleExportExcelFile() {
    return new Promise((resolve, reject) => {
      try {
        resolve(
          (window.location.href =
            '/ezcon/api/createExcel?type=residentAccountNumbers')
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  // 20220311 更新所有保全狀態
  const updateSecurityIp = () => {
    funFind({}).then((result) => {
      console.log(result);
      result.map((i) => {
        axios('/ezcon/api/homeSecurityAddValue', {
          method: 'POST',
          headers: {
            authorization: window.localStorage['feathers-jwt'],
          },
          data: {
            householdNumber: i.householdNumber,
            securityIp: i.securityIp,
          },
        })
          .then((res) => {
            console.log('==home security update Success:', res);
          })
          .catch((error) => {
            console.error('==home security update fail:', error);
          });
        return i;
      });
    });
  };

  // 下載社區QR
  function downloadAllQRcode() {
    // 20220311 更新所有保全狀態
    updateSecurityIp();
    return new Promise((resolve, reject) => {
      try {
        resolve(
          (window.location.href = `/ezcon/api/downloadAccountPDF?access_token=${window.localStorage['feathers-jwt']}`)
        );
      } catch (error) {
        reject(error);
      }
    });
  }
  // 更新及下載個別帳號QR_Code
  function updateDownloadQRcode(record) {
    return new Promise((resolve, reject) => {
      try {
        resolve(
          (window.location.href = `/ezcon/api/updateAutoCreatedAccount?access_token=${window.localStorage['feathers-jwt']}&householdNumbers=${record.householdNumber}`)
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  const disabledDate = (date, view) => {
    switch (view) {
      case 'date':
        return date.isSameOrBefore(yesterday);
      case 'year':
        return date.isBefore(today, 'year');
      case 'month':
        return date.isBefore(today, 'month');
      default:
        return false;
    }
  };

  // 20220406 userName 中間文字顯示〇
  const sliceNameWord = (inWord) => {
    if (typeof inWord !== 'string') return inWord;
    const wdLen = inWord.length;
    let outWord = inWord;
    const wordArr = inWord.split('');
    switch (wdLen) {
      case 1:
        outWord = inWord;
        break;
      case 2:
        outWord = `${wordArr[0]}〇`;
        break;
      case 3:
        outWord = `${wordArr[0]}〇${wordArr[2]}`;
        break;
      case 4:
        outWord = `${wordArr[0]}${wordArr[1]}〇${wordArr[3]}`;
        break;
      default:
        outWord = `${wordArr[0]}〇${[...wordArr]
          .slice(2, wordArr.length)
          .join('')}`;
        break;
    }
    return outWord;
  };

  const getParkingSpaceNumber = useCallback(
    (vehicleType, householdNumber) => {
      const thisHouseParkingSpaces = parkingSpaces.filter((parkingSpace) => {
        return (
          parkingSpace.vehicleType === vehicleType &&
          parkingSpace?.house?.householdNumber === householdNumber
        );
      });

      return thisHouseParkingSpaces.length;
    },
    [parkingSpaces]
  );

  const columnSorter = useCallback(
    (a, b, dataIndex) => {
      let valA = a[dataIndex] ?? '';
      let valB = b[dataIndex] ?? '';

      if (dataIndex === 'car' || dataIndex === 'motorcycle') {
        valA = getParkingSpaceNumber(dataIndex, a['householdNumber']);
        valB = getParkingSpaceNumber(dataIndex, b['householdNumber']);
        return valA - valB;
      }

      if (dataIndex === 'pingsNumber') {
        return Number(valA) - Number(valB);
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return valA - valB;
      }

      return valA.localeCompare(valB);
    },
    [getParkingSpaceNumber]
  );

  const handleAddFormSubmit = async (values) => {
    const data = {
      ...values,
      buyPoint: 0,
      pointPeriod: [],
      finalPoints: 0,
      createdTime: moment().valueOf(),
    };

    try {
      const res = await funCreate(data);
      if (res) {
        updateResidentAccountNumbers();
        closeModal(addForm, 'add');
        Message.success('成功建立戶號!');
      }
    } catch (error) {
      console.error(error);
      Message.error('戶號建立失敗!');
    }
  };

  const handleClickEditButton = useCallback(
    (record) => {
      editForm.setFieldsValue({
        ...record,
        effectiveDate: record.effectiveDate
          ? moment(record.effectiveDate)
          : null,
        usage: record.usage || 'rent',
      });
      setIsEffective(record.effectiveStatus);
      setResidentAccountNumberId(record._id);
      setEditHouseholdNumberModal(true);
    },
    [editForm]
  );

  const tableDataSource = useMemo(() => {
    return residentAccountNumbers;
  }, [residentAccountNumbers]);

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: '戶號',
        dataIndex: 'householdNumber',
        width: 100,
        fixed: 'left',
        sorter: (a, b) => columnSorter(a, b, 'householdNumber'),
      },
      {
        title: '建立時間',
        dataIndex: 'createdTime',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'createdTime'),
        render: (_value, record) => {
          return (
            <p>{moment(record.createdTime).format('YYYY-MM-DD HH:mm:ss')}</p>
          );
        },
      },
      {
        title: '更新時間',
        dataIndex: 'updatedTime',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'updatedTime'),
        render: (_value, record) => {
          return (
            <p>{moment(record.updatedTime).format('YYYY-MM-DD HH:mm:ss')}</p>
          );
        },
      },
      {
        title: '地址',
        dataIndex: 'address',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'address'),
      },
      {
        title: '區權人',
        dataIndex: 'userName',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'userName'),
        render: (value) => {
          return <>{value ? sliceNameWord(value) : null}</>;
        },
      },
      {
        title: '配偶/子女',
        dataIndex: 'familyMembers',
        width: 200,
        render: (_value, record) => {
          return (
            <>
              {record.familyMembers
                ? record.familyMembers.map((list, index) => {
                    return <p key={index}>{sliceNameWord(list)}</p>;
                  })
                : ''}
            </>
          );
        },
      },
      {
        title: '自住/出租',
        dataIndex: 'usage',
        width: 200,
        render: (_value, record) => {
          return <>{record.usage === 'self' ? '自住' : '出租'}</>;
        },
      },
      {
        title: '坪數',
        dataIndex: 'pingsNumber',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'pingsNumber'),
      },
      {
        title: '車牌',
        dataIndex: 'carNumber',
        width: 200,
        render: (_value, record) => {
          return (
            <>
              {record.carNumber
                ? record.carNumber.map((list, index) => {
                    return <p key={index}>{list}</p>;
                  })
                : ''}
            </>
          );
        },
      },
      {
        title: '汽車車位',
        dataIndex: 'householdNumber',
        width: 200,
        render: (houseNum) => {
          const thisHouseParkingSpaces = parkingSpaces.filter(
            (parkingSpace) => {
              return (
                parkingSpace.vehicleType === 'car' &&
                parkingSpace?.house?.householdNumber === houseNum
              );
            }
          );
          if (thisHouseParkingSpaces.length === 0) {
            return '無';
          } else {
            return thisHouseParkingSpaces
              .map((space) => space.parkingNumber)
              .join(', ');
          }
        },
      },
      {
        title: '汽車格數',
        dataIndex: 'householdNumber',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'car'),
        render: (houseNum) => {
          const parkingSpaceNumber = getParkingSpaceNumber('car', houseNum);
          return parkingSpaceNumber;
        },
      },
      {
        title: '機車車位',
        dataIndex: 'householdNumber',
        width: 200,
        render: (houseNum) => {
          const thisHouseParkingSpaces = parkingSpaces.filter(
            (parkingSpace) => {
              return (
                parkingSpace.vehicleType === 'motorcycle' &&
                parkingSpace?.house?.householdNumber === houseNum
              );
            }
          );
          if (thisHouseParkingSpaces.length === 0) {
            return '無';
          } else {
            return thisHouseParkingSpaces
              .map((space) => space.parkingNumber)
              .join(', ');
          }
        },
      },
      {
        title: '機車格數',
        dataIndex: 'householdNumber',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'motorcycle'),
        render: (houseNum) => {
          const parkingSpaceNumber = getParkingSpaceNumber(
            'motorcycle',
            houseNum
          );
          return parkingSpaceNumber;
        },
      },
      {
        title: '電話',
        dataIndex: 'housePhone',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'housePhone'),
      },
      {
        title: '手機',
        dataIndex: 'cellphone',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'cellphone'),
      },
      {
        title: '公司',
        dataIndex: 'company',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'company'),
      },
      {
        title: '承租人',
        dataIndex: 'rentMembers',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'rentMembers'),
        render: (value) => {
          return <>{value ? sliceNameWord(value) : null}</>;
        },
      },
      {
        title: '契約起始',
        dataIndex: 'buyStartTime',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'buyStartTime'),
      },
      {
        title: '契約終止',
        dataIndex: 'buyEndTime',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'buyEndTime'),
      },
      {
        title: '用戶門號',
        dataIndex: 'doorNumber',
        width: 200,
      },
      {
        title: '信箱號碼',
        dataIndex: 'mailNumber',
        width: 200,
      },
      {
        title: '保全裝置IP',
        dataIndex: 'securityIp',
        width: 200,
      },
      {
        title: '操作',
        dataIndex: 'actions',
        width: 220,
        fixed: 'right',
        render: (_value, record) => {
          return (
            <div className={styles.operatingBtn}>
              <Button
                type="normal"
                onClick={() => handleClickEditButton(record)}
              >
                編輯
              </Button>
              <Balloon
                trigger={
                  <Button
                    type="secondary"
                    onClick={() => {
                      updateDownloadQRcode(record);
                    }}
                  >
                    QR_Code
                  </Button>
                }
                closable={false}
              >
                QR_Code遺失或過期可點選
              </Balloon>
            </div>
          );
        },
      },
    ];

    if (allowedFuncs.includes('財務管理')) {
      columns.splice(8, 0, {
        title: '繳費機制',
        dataIndex: 'pay',
        width: 200,
        sorter: (a, b) => columnSorter(a, b, 'pay'),
      });
    }
    if (allowedFuncs.includes('財務管理')) {
      columns.splice(columns.length - 2, 0, {
        title: '管理費生效日期',
        dataIndex: 'effectiveDate',
        width: 200,
        render: (_value, record) => {
          // 暫時註解財務功能
          return (
            <>
              {record.effectiveDate
                ? moment(record.effectiveDate).format('YYYY/MM/DD')
                : ''}
            </>
          );
        },
      });
    }
    if (allowedFuncs.includes('送件服務')) {
      columns.splice(columns.length - 2, 0, {
        title: '機器人停靠位置',
        dataIndex: 'robotPosition',
        width: 200,
      });
    }

    return columns;
  }, [
    allowedFuncs,
    columnSorter,
    getParkingSpaceNumber,
    handleClickEditButton,
    parkingSpaces,
  ]);

  const closeModal = (form, formType) => {
    form.resetFields();
    setIsEffective(false);
    if (formType === 'add') {
      setAddHouseholdNumberModal(false);
    } else {
      setEditHouseholdNumberModal(false);
    }
  };

  const handleEditFormSubmit = async (values) => {
    const { effectiveDate } = values;
    const formData = {
      ...values,
      _id: residentAccountNumberId,
      effectiveDate: effectiveDate ? effectiveDate.valueOf() : null,
      updatedTime: moment().valueOf(),
    };

    try {
      await residentAccountNumbersService.patch(
        residentAccountNumberId,
        formData
      );
      updateResidentAccountNumbers();
      closeModal(editForm, 'edit');
      Message.success('資料更新成功!');
    } catch (error) {
      console.error(error);
      Message.error('資料更新失敗!');
    }

    if (values.securityIp) {
      try {
        const res = await axios('/ezcon/api/homeSecurityAddValue', {
          method: 'POST',
          headers: {
            authorization: window.localStorage['feathers-jwt'],
          },
          data: {
            householdNumber: values.householdNumber,
            securityIp: values.securityIp,
          },
        });
        console.log('==send message Success:', res);
      } catch (error) {
        console.error('==send message fail:', error);
      }
    }
  };

  const changeEffectiveDate = (form, changedValues) => {
    if (changedValues.effectiveDate) {
      form.setFieldValue('effectiveStatus', true);
      setIsEffective(true);
    } else {
      form.setFieldsValue({ effectiveStatus: false, pay: '' });
      setIsEffective(false);
    }
  };

  const changeEffectiveStatus = (form, changedValues) => {
    const effectiveStatus = changedValues.effectiveStatus;
    const effectiveDate = form.getFieldValue('effectiveDate');

    if (effectiveStatus && effectiveDate === null) {
      form.setFieldValue('effectiveDate', moment());
    }
    if (effectiveStatus === false) {
      form.setFieldsValue({ effectiveDate: null, pay: '' });
    }
    setIsEffective(changedValues.effectiveStatus);
  };

  const handleFormValuesChange = (form, changedValues) => {
    if ('effectiveDate' in changedValues) {
      changeEffectiveDate(form, changedValues);
    }
    if ('effectiveStatus' in changedValues) {
      changeEffectiveStatus(form, changedValues);
    }
  };

  const removeResident = async (householdNumber, residentAccountNumberId) => {
    const result = await Swal.fire({
      title: `確定刪除戶號(${householdNumber})資料?`,
      text: '刪除後將無法復原!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#BEBEBE',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    });
    if (result.isConfirmed) {
      try {
        await residentAccountNumbersService.remove(residentAccountNumberId);
        updateResidentAccountNumbers();
        closeModal(editForm, 'edit');
        Message.success('成功刪除戶號!');
      } catch (error) {
        console.error(error);
        Message.error('刪除戶號失敗!');
      }
    }
  };

  return (
    <>
      <div className={styles.btn}>
        <Button
          type="normal"
          onClick={() => {
            setAddHouseholdNumberModal(true);
          }}
        >
          建立戶號
        </Button>
        <Upload
          showUploadList={false}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          customRequest={async (options) => {
            const { file } = options;
            const formData = new FormData();
            formData.append('files', file);
            try {
              await axios.post('/ezcon/api/residentExcelUpadte', formData, {
                headers: {
                  authorization: window.localStorage['feathers-jwt'],
                },
              });
              Message.success('上傳成功');
              updateResidentAccountNumbers();
            } catch (error) {
              console.error(error);
              Message.error('上傳失敗');
            }
          }}
        >
          <Button className={styles.exportBtn} type="secondary">
            匯入Excel
          </Button>
        </Upload>
        <Button
          type="secondary"
          className={styles.exportBtn}
          onClick={downloadExampleExcel}
        >
          下載範例表單
        </Button>
        <Button
          type="secondary"
          className={styles.exportBtn}
          onClick={handleExportExcelFile}
        >
          匯出Excel
        </Button>

        <Button
          type="secondary"
          className={styles.exportBtn}
          onClick={downloadAllQRcode}
        >
          下載QR
        </Button>
        <DownloadButton
          url={'/ezcon/api/downloadEzconStyleQrcode'}
          responseFileType={'blob'}
          fileName={`住戶信封_${moment().format('YYYYMMDD')}.pdf`}
          buttonProps={{ type: 'secondary' }}
        >
          下載信封
        </DownloadButton>
      </div>
      <Modal
        title="建立資料"
        centered
        bodyStyle={modalBodyStyle}
        className={styles.dialog}
        open={addHouseholdNumberModal}
        footer={null}
        onCancel={() => {
          closeModal(addForm, 'add');
        }}
      >
        <Form
          form={addForm}
          onFinish={handleAddFormSubmit}
          onValuesChange={(changedValues) =>
            handleFormValuesChange(addForm, changedValues)
          }
          layout="vertical"
          initialValues={formInitValues}
        >
          <Item
            label="戶號"
            name="householdNumber"
            help="範例:A-1-1 依序填入棟別、樓層、子項目"
            rules={[{ required: true }]}
          >
            <Input placeholder="請輸入戶號" />
          </Item>
          <Item label="地址" name="address" help="範例:3段1號">
            <Input placeholder="請輸入地址" />
          </Item>

          <Item label="區權人" name="userName">
            <Input placeholder="請輸入區權人名稱" />
          </Item>

          <Item label="配偶/子女" name="familyMembers">
            <Select mode="tags" placeholder="請輸入配偶/子女 按Enter確認" />
          </Item>
          <Item label="自住/出租" name="usage">
            <RadioGroup>
              <Radio id="self" value="self">
                自住
              </Radio>
              <Radio id="rent" value="rent">
                出租
              </Radio>
            </RadioGroup>
          </Item>
          <Item
            label="車牌"
            name="carNumber"
            help="範例:ABC-138 可填入多組車牌號碼"
          >
            <Select mode="tags" placeholder="請輸入車牌號碼 按Enter確認" />
          </Item>
          {allowedFuncs.includes('財務管理') && (
            <Item // 暫時註解財務功能
              label=" 管理費生效日期"
            >
              <Space align="baseline">
                <Item name="effectiveDate">
                  <DatePicker disabledDate={disabledDate} />
                </Item>
                <Item name="effectiveStatus" valuePropName="checked">
                  <Checkbox>勾選該住戶即可生效</Checkbox>
                </Item>
              </Space>
            </Item>
          )}
          {allowedFuncs.includes('財務管理') && isEffective && (
            <Item // 暫時註解財務功能
              label="繳費機制"
              name="pay"
              rules={[{ required: true, message: '請選擇繳費機制' }]}
            >
              <RadioGroup>
                <Radio id="monthPay" value="月繳">
                  月繳
                </Radio>
                <Radio id="yearPay" value="年繳">
                  年繳
                </Radio>
              </RadioGroup>
            </Item>
          )}
          <Item
            label="坪數"
            name="pingsNumber"
            help="坪數只能填數字"
            rules={[{ required: true }]}
          >
            <Input placeholder="請輸入坪數" />
          </Item>
          <Item
            label="電話"
            name="housePhone"
            help="範例:02-1234571 依序填入電話號碼"
          >
            <Input placeholder="請輸入電話" />
          </Item>
          <Item
            label="手機"
            name="cellphone"
            help="範例:09-12345682 依序填入手機號碼"
          >
            <Input placeholder="請輸入手機" />
          </Item>
          <Item label="公司" name="company">
            <Input placeholder="請輸入公司名稱" />
          </Item>
          <Item label="承租人" name="rentMembers">
            <Input placeholder="請輸入承租人名稱" />
          </Item>
          <Item label="契約起始" name="buyStartTime" help="範例:2021/4/30 ">
            <Input placeholder="請輸入契約起始" />
          </Item>
          <Item label="契約終止" name="buyEndTime" help="範例:2021/4/30 ">
            <Input placeholder="請輸入契約終止" />
          </Item>
          <Item label="用戶門號" name="doorNumber">
            <Input placeholder="請輸入用戶門號" />
          </Item>
          <Item label="信箱號碼" name="mailNumber">
            <Input placeholder="請輸入信箱號碼" />
          </Item>
          <Item label="保全裝置IP" name="securityIp">
            <Input placeholder="請輸入保全裝置IP" />
          </Item>
          <Item
            name="isIgnoreFinanceBarcode"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>勾選不產生帳單繳費條碼</Checkbox>
          </Item>
          {allowedFuncs.includes('送件服務') && (
            <Item label="機器人的停靠位置" name="robotPosition">
              <Input placeholder="請輸入機器人的停靠位置" />
            </Item>
          )}
          <Item>
            <Space className={styles.formFooterButtons}>
              <Button type="primary" htmlType="submit">
                送出
              </Button>
              <Button type="normal" onClick={() => closeModal(addForm, 'add')}>
                取消
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
      <Card free>
        <Card.Content>
          <Table
            columns={tableColumns}
            dataSource={tableDataSource}
            pagination={{ pageSize: 50 }}
            scroll={{ y: '60vh' }}
            rowKey="_id"
          >
            {/* <Table.Column title="順序" dataIndex="sort" width={100} lock='left' /> */}
            {/* <Table.Column
              sortable
              title="機車格數"
              dataIndex="scooterNum"
              width={200}
            /> */}
            {/* <Table.Column  title="點數" dataIndex="finalPoints" width={200} /> */}
          </Table>
        </Card.Content>
      </Card>
      <Modal
        title="編輯資料"
        centered
        bodyStyle={modalBodyStyle}
        className={styles.dialog}
        open={editHouseholdNumberModal}
        footer={null}
        onCancel={() => {
          updateResidentAccountNumbers();
          closeModal(editForm, 'edit');
        }}
      >
        {/* 20220323 繳費機制＆生效日期依照是否有選擇財物顯示 */}
        <Form
          form={editForm}
          onFinish={handleEditFormSubmit}
          onValuesChange={(changedValues) =>
            handleFormValuesChange(editForm, changedValues)
          }
          layout="vertical"
          initialValues={formInitValues}
        >
          <Item
            label="戶號"
            name="householdNumber"
            help="範例:A-1-1 依序填入棟別、樓層、子項目"
            rules={[{ required: true }]}
          >
            <Input
              disabled={user.userGroup.name !== 'superAdmin'}
              placeholder="請輸入戶號"
            />
          </Item>
          <Item label="地址" name="address">
            <Input
              disabled={user.userGroup.name !== 'superAdmin'}
              placeholder="請輸入地址"
            />
          </Item>
          <Item label="區權人" name="userName">
            <Input placeholder="請輸入區權人名稱" />
          </Item>
          <Item label="配偶/子女" name="familyMembers">
            <Select mode="tags" placeholder="請輸入配偶/子女 按Enter確認" />
          </Item>
          <Item label="自住/出租" name="usage">
            <RadioGroup>
              <Radio id="self" value="self">
                自住
              </Radio>
              <Radio id="rent" value="rent">
                出租
              </Radio>
            </RadioGroup>
          </Item>
          <Item label="車牌" name="carNumber">
            <Select mode="tags" placeholder="請輸入車牌號碼 按Enter確認" />
          </Item>
          {allowedFuncs.includes('財務管理') && (
            <Item // 暫時註解財務功能
              label="管理費生效日期"
            >
              <Space align="baseline">
                <Item name="effectiveDate">
                  <DatePicker disabledDate={disabledDate} />
                </Item>
                <Item name="effectiveStatus" valuePropName="checked">
                  <Checkbox>勾選該住戶即可生效</Checkbox>
                </Item>
              </Space>
            </Item>
          )}
          {allowedFuncs.includes('財務管理') && isEffective && (
            <Item // 暫時註解財務功能
              label="繳費機制"
              name="pay"
              rules={[{ required: true, message: '請選擇繳費機制' }]}
            >
              <RadioGroup>
                <Radio id="monthPay" value="月繳">
                  月繳
                </Radio>
                <Radio id="yearPay" value="年繳">
                  年繳
                </Radio>
              </RadioGroup>
            </Item>
          )}
          <Item
            label="坪數"
            name="pingsNumber"
            help="坪數只能填數字"
            rules={[{ required: true }]}
          >
            <Input
              disabled={user.userGroup.name !== 'superAdmin'}
              placeholder="請輸入坪數"
            />
          </Item>
          <Item label="電話" name="housePhone">
            <Input placeholder="請輸入電話" />
          </Item>
          <Item label="手機" name="cellphone">
            <Input placeholder="請輸入手機" />
          </Item>
          <Item label="公司" name="company">
            <Input placeholder="請輸入公司名稱" />
          </Item>
          <Item label="承租人" name="rentMembers">
            <Input placeholder="請輸入承租人名稱" />
          </Item>
          <Item label="契約起始" name="buyStartTime">
            <Input placeholder="請輸入契約起始" />
          </Item>
          <Item label="契約終止" name="buyEndTime">
            <Input placeholder="請輸入契約終止" />
          </Item>
          <Item label="用戶門號" name="doorNumber">
            <Input placeholder="請輸入用戶門號" />
          </Item>
          <Item label="信箱號碼" name="mailNumber">
            <Input
              disabled={user.userGroup.name !== 'superAdmin'}
              placeholder="請輸入信箱號碼"
            />
          </Item>
          <Item label="保全裝置IP" name="securityIp">
            <Input
              disabled={user.userGroup.name !== 'superAdmin'}
              placeholder="請輸入保全裝置IP"
            />
          </Item>
          <Item name="isIgnoreFinanceBarcode" valuePropName="checked">
            <Checkbox>勾選不產生帳單繳費條碼</Checkbox>
          </Item>
          {allowedFuncs.includes('送件服務') && (
            <Item label="機器人的停靠位置" name="robotPosition">
              <Input placeholder="請輸入機器人的停靠位置" />
            </Item>
          )}
          <Item>
            <Space className={styles.formFooterButtons}>
              <Button type="primary" htmlType="submit">
                確認
              </Button>
              <Button onClick={() => closeModal(editForm, 'edit')}>取消</Button>
              {user.userGroup.name === 'superAdmin' && (
                <Button
                  type="primary"
                  warning
                  onClick={() =>
                    removeResident(
                      editForm.getFieldValue('householdNumber'),
                      residentAccountNumberId
                    )
                  }
                >
                  刪除
                </Button>
              )}
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddResidentAccountNumbers;
